import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { isNewGame, type GameId } from '@tg/shared';
import { useSession } from '@/stores/session';
import { cx } from '@/ui';
import { usePassport, useStamped } from './usePassport';
import s from './passport.module.css';

/** «new» — a signed-in player has no stamp of the game; «fresh» — a guest sees a game added within 14 days */
export type GameMark = 'new' | 'fresh' | null;

export function useGameMarks(): (g: GameId) => GameMark {
  const status = useSession((st) => st.status);
  const loaded = usePassport((st) => st.data !== null);
  const stamped = useStamped();
  return useCallback(
    (g: GameId): GameMark => {
      if (status === 'user') return loaded && !stamped.has(g) ? 'new' : null;
      if (status === 'guest') return isNewGame(g, Date.now()) ? 'fresh' : null;
      return null;
    },
    [status, loaded, stamped],
  );
}

/**
 * The mark itself: `card` — a pill on the cover corner, `inline` — a pill after a title, `dot` — a teal dot in the
 * left menu (its text is for screen readers and the tooltip). Teal accent, never red (red means a win in zh).
 */
export function NewMark({ game, variant = 'inline', className }: { game: GameId; variant?: 'card' | 'inline' | 'dot'; className?: string }) {
  const intl = useIntl();
  const kind = useGameMarks()(game);
  if (!kind) return null;
  const text = intl.formatMessage({ id: `passport.mark.${kind}` });
  const label = intl.formatMessage({ id: `passport.mark.${kind}Label` }, { game: intl.formatMessage({ id: `common.game.${game}` }) });
  if (variant === 'dot')
    return (
      <span className={cx(s.markDot, className)} title={label} data-mark={kind}>
        <span className={s.srOnly}>{text}</span>
      </span>
    );
  return (
    <span className={cx(s.mark, variant === 'card' && s.markCard, kind === 'fresh' && s.markFresh, className)} title={label} data-mark={kind}>
      {text}
    </span>
  );
}
